import { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

// Assets
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import addIcon from "../../assets/add_icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import { FailedIcon, DoneIcon, WarningIcon } from "../../assets/registtyIcons/icons";

// Utils
import { DataContext } from "../../utils/DataContext";
import { UserAuth } from "../../utils/AuthContext";
import useLanguage from "../../utils/useLanguage";
import { formatDate } from "../../utils/toDate";

// Styles
import classes from "../../components/Modal.module.css";

// Components
import PDFUploader from "../../components/PDFUploader";
import BackToDashboard from "../../components/BackToDashboard";
import ErrorBox from "../../components/ErrorBox";
import Loader from "../../components/Loader";

import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import SectionDivider from "../../components/SectionDivider";
import HotDropdown from "../../components/HotDropdown";
import AddAnotherButton, { GENDER } from "../../components/AddAnotherButton";
import CollapsibleCard from "../attendance-system/CollapsibleCard";
import PageWrapper from "../../components/PageWrapper";
import { getSupplierById } from "../../services/suppliers/suppliersServices";
import HotdropdownV2 from "../../components/HotdropdownV2";
import AreYouSureModal from "../../components/AreYouSureModal";
import { getAllBanks } from "../../services/bank/bankServices";
import RenderMatchStatus from "../../components/RenderMatchStatus";
import RenderMatchPercentage from "../../components/RenderMatchPercentage";
import ErrorMessage from "../../components/ErrorMessage";

const AnalyzedNoCustomerInvoice = ({ setShowInputFields }) => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const navigate = useNavigate();
  const containerDiv = useRef(null);

  const location = useLocation();
  const extractedData = location.state?.extractedData;

  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const pdfFile = location.state?.pdfFile;

  const [supplierWebsite, setSupplierWebsite] = useState({
    URL: extractedData?.websiteUrl,
    urlStatus: extractedData?.urlStatus,
  });

  // STATE FOR RADIO BUTTON SELECTION
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedBankSource, setSelectedBankSource] = useState("");
  const [selectedAccountNumberSource, setSelectedAccountNumberSource] =
    useState("");
  const [stateErrors, setStateErrors] = useState({
    selectedSource: false,
    selectedBankSource: false,
    selectedAccountNumberSource: false,
  });
  const [branchAddressSource, setBranchAddressSource] = useState("");

  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState({
    accountNumber:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.accountNumber ||
      "",
    id:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank?.id || "",
    title:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.accountNumber ||
      "",
  });

  // Bank related states
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState({
    value:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank?.id || "",
    title:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank?.name ||
      "",
    address:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank?.address ||
      "",
  });

  const [selectedBankBranches, setSelectedBankBranches] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState({
    address:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank?.branch[0]
        .address || "",
    value:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank?.branch[0]
        .id || "",
  });

  const [bankAccountNumber, setBankAccountNumber] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [containerToDelete, setContainerToDelete] = useState(null);

  const handleRadioChange = (source) => {
    setSelectedSource(source);
  };

  const handleBankRadioChange = (source) => {
    setSelectedBankSource(source);
  };

  const validateStates = () => {
    const errors = {
      selectedSource: !selectedSource, // Check if `selectedSource` is empty
      selectedBankSource: !selectedBankSource, // Check if `selectedBankSource` is empty
      selectedAccountNumberSource: !selectedAccountNumberSource, // Check if `selectedAccountNumberSource` is empty
    };

    setStateErrors(errors);

    // Return true if all states are valid
    return !Object.values(errors).some((error) => error);
  };

  // NEW: State for Company Details
  const [companyDetails, setCompanyDetails] = useState({
    name: extractedData?.["supplierName"],
    address: extractedData?.["supplierAddress"],
    email: extractedData?.["Email"],
    phone: extractedData?.["Phone Number"],
  });

  const [supplierDBDetails, setSupplierDBDetails] = useState({
    id: extractedData?.["Supplier Info from DB"]?.id || "",
    name: extractedData?.["Supplier Info from DB"]?.name || "",
    address: extractedData?.["Supplier Info from DB"]?.address || "",
    email: extractedData?.["Supplier Info from DB"]?.email || "",
    phone: extractedData?.["Supplier Info from DB"]?.phoneNumber || "",
  });

  const [bankDBDetails, setBankDBDetails] = useState({
    id:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank?.id || "",
    name:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank?.name ||
      "",
    address:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank?.address ||
      "",
    branch: {
      address:
        extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank
          ?.branch[0].address || "",
      branchId:
        extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.bank
          ?.branch[0].id || "",
    },
    dbAccountNumber:
      extractedData?.["Supplier Info from DB"]?.bankAccount[0]?.accountNumber ||
      "",
  });

  // NEW: State for Bank Details
  const [bankDetails, setBankDetails] = useState({
    name: extractedData?.["Bank Name"],
    address: extractedData?.["Bank Address"],
    accountNumber: extractedData?.["account_number"],
  });

  const [invoice, setInvoice] = useState({
    invoiceNumber: extractedData?.["Invoice Number"],
    amountForSupplier: parseInt(extractedData?.totalInvoiceAmount || 0),
    date: extractedData?.["Invoice Date"]
      ? extractedData["Invoice Date"]
      : new Date(),
    notes: "",
    version: extractedData?.version || 1,
    calculatedContainersPrice: extractedData?.totalContainersValue,
    invoiceContainersPrice: extractedData?.totalInvoiceAmount,
  });

  useEffect(() => {
    const handleUpload = async (event) => {
      if (event) event.preventDefault();

      if (!pdfFile) {
        navigate("/");
        return;
      }

      const formData = new FormData();
      formData.append("file", pdfFile);

      try {
        const uploadResponse = await axios.post(
          `${process.env.REACT_APP_URL}/extractPdf`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${userData?.token}`,
              "User-Id": userData?.userId,
              "Supplier-Id": selectedSupplier?.id,
            },
          }
        );

        if (uploadResponse.status === 200) {
          setInvoice({
            invoiceNumber: uploadResponse?.data?.["Invoice Number"],
            amountForSupplier: uploadResponse?.data?.totalInvoiceAmount,
            date:
              new Date(uploadResponse?.data?.["Invoice Date"]) || new Date(),
            notes: uploadResponse?.data?.notes,
            version: uploadResponse?.version,
          });
        }
      } catch (error) {
        console.error("Upload failed:", error);
      } finally {
        setAnalyzeLoading(false);
      }
    };

    // handleUpload();
  }, []);

  const containers = extractedData?.Containers?.filter(
    (container) =>
      typeof container.containerNumber === "string" &&
      container.containerNumber.trim() !== ""
  )?.map((container) => ({
    amountForSupplier: parseInt(container.totalValue || 0),
    containerNumber: container.containerNumber,
    material: container.material,
    numberOfBoxes: container.quantity,
    unitPrice: container.unitPrice,
    expectedArrivalDate: new Date(),
    permitsReceivingDate: new Date(),
    dateOfEntry: new Date(),
    shippingDate: new Date(),
    exitDate: new Date(),
    unit: container?.unit,
    unitPriceCurrency: container?.unitPriceCurrency,
  }));

  // * Only containers data
  const [inputs, setInputs] = useState(containers || []);

  const {
    suppliers,
    getSuppliers,
    setSupplierModal,
    selectedSupplier,
    error,
    setError,
    shippedContainers,
    setShippedContainers,
    handleSelectSupplier,
    setSupplierFields,
    setNetworkError,
    setShippedContainersCount,
    subCompanies,
    selectedSubCompany,
    handleSelectSubCompany,
    getSubCompanies,
  } = useContext(DataContext);

  const { userData } = useContext(UserAuth);

  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const pdfUploaderRef = useRef(null);

  const [howWasPapersReceivedStatus, setHowWasPapersReceivedStatus] = useState({
    title: "receivedByUs",
    value: "receivedByUs",
  });

  const [totalInvoiceAmount, setTotalInvoiceAmount] = useState(""); // store the received globally
  const [loadingModal, setLoadingModal] = useState(false);

  const [page, setPage] = useState(1);

  const handleAddInput = (e) => {
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
    const newContainer = {
      containerNumber: "",
      amountForSupplier: "",
      expectedArrivalDate: new Date(),
      finalDestination: "",
      material: "",
      unitPrice: "",
      numberOfBoxes: 0,
      measuringUnit: "",
      shipper: "",
      shippingDate: new Date(),
      exitDate: new Date(),
      dateOfEntry: new Date(),
      howWasPapersReceived: "",
      permitsReceivingDate: new Date(),
      notes: "",
    };

    setInputs((prevInputs) => {
      const updatedInputs = [...prevInputs, newContainer];

      // Add null check here
      if (extractedData && extractedData.Containers) {
        extractedData.Containers = [
          ...extractedData.Containers,
          { container: newContainer },
        ];
      }

      setPage(prevInputs?.length + 1);
      return updatedInputs;
    });

    // setInputs((prevInputs) => {
    //   const updatedInputs = [...prevInputs, newContainer];
    //   extractedData.Containers = [
    //     ...extractedData.Containers,
    //     { container: newContainer },
    //   ];
    //   setPage(prevInputs?.length + 1);
    //   return updatedInputs;
    // });
  };

  const handleInputChange = (e, pageIndex) => {
    const { name, value } = e.target;

    setInputs((prevInputs) => {
      return prevInputs.map((item, index) =>
        index === pageIndex ? { ...item, [name]: value } : item
      );
    });
    // extractedData.Containers = extractedData.Containers.map((item, index) =>
    //   index === pageIndex
    //     ? { container: { ...item.container, [name]: value } }
    //     : item
    // );
  };

  const [received, setReceived] = useState(extractedData?.totalInvoiceAmount);

  const handleReceivedChange = (e) => {
    const { value } = e.target;
    setReceived(value);
  };

  const handleNext = () => {
    if (page < inputs.length) setPage(page + 1);
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1);
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    const name = e.target?.name;

    const updatedInputs = [...inputs];
    updatedInputs[page - 1][name] = new Date(date);
    setInputs(updatedInputs);
  };

  const handleAddRow = async () => {
    try {
      const token = userData?.token;
      const userId = parseInt(userData?.userId, 10);
      const organizationId = parseInt(userData?.organizationId, 10);

      if (!userId) {
        console.error("User ID not found in localStorage");
        return;
      }

      if (!validateStates()) {
        window.scroll(0, 0); // Scroll to the top to show the error box
        return;
      }

      if (!invoice?.invoiceNumber) {
        setError("invalidInvoiceNumber");
        window.scroll(0, 0);
        return;
      }

      setLoadingModal(true);
      setError("");

      const payload = {
        organizationId,
        supplierId: selectedSource === "invoice" ? null : supplierDBDetails?.id,
        subCompanyId:
          selectedSubCompany?.id !== "All" ? selectedSubCompany?.id : null,
        createdBy: userId,
        ...invoice,

        // Send company details based on selected source
        isNewAccount: selectedAccountNumberSource === "invoice" ? true : false,
        supplierDetails: {
          type: selectedSource === "invoice" ? "invoice" : "db",
          ...(selectedSource === "invoice"
            ? companyDetails
            : supplierDBDetails),
          company:
            selectedSource === "invoice"
              ? companyDetails.name
              : supplierDBDetails.name,
          currency: "USD",
          accountNumber:
            selectedAccountNumberSource === "invoice"
              ? bankDetails.accountNumber
              : selectedBankAccount.accountNumber,
          supplierId: supplierDBDetails?.id,
          websiteUrl: supplierWebsite.websiteUrl,
        },
        bankDetails: {
          ...(selectedBankSource === "invoice" ? bankDetails : bankDBDetails),
          bankId: selectedBankSource === "invoice" ? "" : selectedBank.value,
          branchId:
            selectedBankSource === "invoice" ? "" : selectedBranch.value,
          name:
            selectedBankSource === "invoice"
              ? bankDetails.name
              : selectedBank.title,
          address:
            selectedBankSource === "invoice"
              ? bankDetails.address
              : branchAddressSource === "invoice"
              ? bankDetails.address
              : selectedBranch.title,
          type:
            selectedBankSource === "invoice"
              ? "invoice"
              : branchAddressSource === "invoice"
              ? "db-mixed"
              : "db",
        },

        // containers
        containers: inputs?.filter((input) => input?.containerNumber),
      };

      // Prepare training data (only invoice-extracted data)
      const trainingPayload = {
        invoiceNumber: invoice?.invoiceNumber,
        invoiceDate: invoice?.date,
        amountForSupplier: invoice?.amountForSupplier,
        notes: invoice?.notes,
        supplierName: companyDetails.name,
        supplierAddress: companyDetails.address,
        supplierEmail: companyDetails.email,
        supplierPhone: companyDetails.phone,
        bankName: bankDetails.name,
        bankAddress: bankDetails.address,
        accountNumber: bankDetails.accountNumber,
        containers: inputs
          ?.filter((input) => input?.containerNumber)
          .map((container) => ({
            containerNumber: container.containerNumber,
            amountForSupplier: container.amountForSupplier,
            material: container.material,
            numberOfBoxes: container.numberOfBoxes,
            unitPrice: container.unitPrice,
            unit: container.unit,
            unitPriceCurrency: container.unitPriceCurrency,
          })),
      };

      setError("");
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/multiContainerInvoiceWithoutC`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "Supplier-Id": selectedSupplier?.id,
          },
        }
      );

      if (response.status === 200) {
        // Send training data
        try {
          await axios.post(
            `${process.env.REACT_APP_AI_API}/training/data`,
            trainingPayload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "User-Id": userId,
                "Supplier-Id": selectedSupplier?.id,
              },
            }
          );
        } catch (trainingError) {
          console.error("Training data export failed:", trainingError);
          // Don't block the main flow if training export fails
        }

        setShippedContainers(response.data.containers);
        handleChildUploadDocs(response?.data?.invoice?.id);
        navigate("/noCustomerInvoices");
        setError("");
      }
    } catch (error) {
      const errMsg = error.response?.data?.message;
      if (errMsg === "alreadyLinkedToAnInvoice") {
        const containerNumber = error?.response?.data?.containerNumber;
        setError(`${t("container")} ${containerNumber} ${t(errMsg)}`);
      } else if (errMsg === "pleaseSelectASubcompany") {
        setError(errMsg);
      } else {
        console.log(error);
        setError(errMsg || "An error occurred");
      }
      window.scroll(0, 0);
    } finally {
      setLoadingModal(false);
    }
  };

  const getSupplier = async () => {
    try {
      const response = await getSupplierById(supplierDBDetails?.id);
      const supplierData = response.data; // or however your API returns the data

      // Update supplierDBDetails with the fetched data
      setSupplierDBDetails({
        id: supplierData?.id || "",
        name: supplierData?.name || "",
        address: supplierData?.address || "",
        email: supplierData?.email || "",
        phone: supplierData?.phoneNumber || "",
      });
    } catch (error) {
      console.error("Error fetching supplier:", error);
    }
  };

  const handleAmountChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => {
      let updatedValues = { [name]: value };

      return {
        ...prev,
        ...updatedValues,
      };
    });
  };

  const handleCancel = () => {
    // setShowInputFields(false);
    setInputs([
      {
        containerNumber: "",
        expectedArrivalDate: new Date(),
        finalDestination: "",
        shipper: "",
        material: "",
        numberOfBoxes: 0,
        measuringUnit: "",
        exitDate: new Date(),
        dateOfEntry: new Date(),
        shippingDate: new Date(),
        permitsReceivingDate: new Date(),
        amountForSupplier: "",
        notes: "",
      },
    ]);
    navigate("/noCustomerInvoices");
  };

  const handleInvoiceInputChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: value }));
  };

  // NEW: Handler for company details
  const handleCompanyDetailsChange = (e) => {
    const { name, value } = e.target;
    setCompanyDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompanyDBDetailsChange = (e) => {
    const { name, value } = e.target;
    setSupplierDBDetails((prev) => ({ ...prev, [name]: value }));
  };

  // NEW: Handler for bank details
  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    setBankDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvoiceDateChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: new Date(value) }));
  };

  const handleChildUploadDocs = (multiContainersInvoiceId) => {
    if (pdfUploaderRef.current) {
      pdfUploaderRef.current.handleUploadDocs(multiContainersInvoiceId);
    }
  };

  const handleDeleteContainerClick = (e) => {
    const containerIndex = parseInt(
      e?.target?.getAttribute("data-container-index")
    );
    setContainerToDelete(containerIndex);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (containerToDelete !== null) {
      setInputs((prevInputs) =>
        prevInputs.filter((_, index) => index !== containerToDelete)
      );
      setContainerToDelete(null);
      setShowDeleteModal(false);
    }
  };

  const handleCancelDelete = () => {
    setContainerToDelete(null);
    setShowDeleteModal(false);
  };

  const handleDeleteContainer = (e) => {
    const containerIndex = parseInt(
      e?.target?.getAttribute("data-container-index")
    );

    const filteredInputs = inputs?.filter(
      (input, index) => index !== containerIndex
    );

    setInputs(filteredInputs);
    handlePrevious();
  };

  const getAccounts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getBankAccounts`,
        {
          params: {
            supplierId: supplierDBDetails?.id,
          },
        }
      );

      setBankAccounts(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleBranchSelection = (branch) => {
    setSelectedBranch(branch);
    setSelectedBankAccount(null); // Reset account when branch changes
    setSelectedBankSource("db"); // Set radio button selection

    // Update bank DB details with branch info
    setBankDBDetails((prev) => ({
      ...prev,
      branch: {
        address: branch.title,
        branchId: branch.value,
      },
    }));
  };

  const getFilteredAccounts = () => {
    if (!selectedBranch?.value) return bankAccounts;
    const filteredBankAccounts = bankAccounts?.filter(
      (account) => account.branchId === selectedBranch.value
    );

    return filteredBankAccounts;
  };

  const getBanks = async (page = 1, limit = 5, undefined, searchQuery) => {
    try {
      const response = await getAllBanks({
        page: page,
        limit: limit,
        searchQuery: searchQuery,
        isIraqi: false,
      });

      if (response.status === 200) {
        setBanks(response.data.banks);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectABranch = (selectedBank) => {
    setSelectedBank(selectedBank);
    // Use the parameter directly instead of relying on state
    const branches = banks.filter((bank) => bank.id === selectedBank.value);
    setSelectedBankBranches(branches[0]?.branch || []);
    // Reset dependent states when bank changes
    setSelectedBranch(null);
    setSelectedBankAccount(null);
  };

  const handleBankAccountSelection = (bankAccount) => {
    setSelectedBankAccount(bankAccount);
    // setBankDBDetails({
    //   name: bankAccount.bankName,
    //   address: bankAccount.bankAddress,
    //   accountNumber: bankAccount.accountNumber,
    // });
  };

  useEffect(() => {
    if (supplierDBDetails?.id) {
      getSupplier();
      getAccounts();
    }
    getBanks();
  }, [supplierDBDetails?.id, selectedSupplier.id]);

  return (
    <PageWrapper>
      <div className="add-shipped-container-page">
        <div className="add-shipped-container-top-actions">
          <div className="user-logout">
            <BackToDashboard
              title={t("back")}
              variant="blank"
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={14}
            />
          </div>
        </div>
        <h3 className={"add-shipped-container-title"}>{t("createInvoice")}</h3>
        {loadingModal ? <Loader /> : null}
        {analyzeLoading && <Loader />}

        {/* SOURCE ERRORS */}
        <>
          {Object.values(stateErrors).some((error) => error) && (
            <ErrorBox>{t("pleaseSelectAllRequiredOptions")}</ErrorBox>
          )}
        </>

        {error && !loadingModal && (
          <div className="u-width_fit-content u-margin-center">
            <ErrorBox>{t(error)}</ErrorBox>
          </div>
        )}
        <div className="add-shipped-container-invoice-part">
          <div className={classes.inputContainer}>
            <span>{t("subCompany")}: </span>
            <Dropdown
              options={subCompanies}
              selected={selectedSubCompany}
              setSelected={handleSelectSubCompany}
              flex={true}
              getData={getSubCompanies}
              nameInAr={true}
              thick
            />
          </div>
          {/* WEBSITE SECTION */}
          <SectionDivider title="websiteDetails" />
          <CollapsibleCard
            title={t("website")}
            collapsed={false}
            body={
              <>
                {/* Website details */}
                <div className="o-column o-column--gap-size_300 u-flex_flex-1 u-padding--top_300">
                  <SectionDivider title="websiteDetails" />
                  <section className="o-column-row-switcher o-row o-row--gap-size_100">
                    <span className="min-width-150">{t("website")}: </span>
                    {supplierWebsite?.URL ? (
                      <a
                        href={
                          supplierWebsite?.URL &&
                          supplierWebsite.URL.startsWith("http")
                            ? supplierWebsite.URL
                            : `http://${supplierWebsite?.URL}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-color t-bold_bold"
                      >
                        {supplierWebsite?.URL}
                      </a>
                    ) : (
                      <span className="not-working-website">
                        {t(supplierWebsite?.urlStatus)}
                      </span>
                    )}
                  </section>
                  <section className="o-row">
                    <span className="min-width-150">
                      {t("websiteStatus")}:{" "}
                    </span>

                    {supplierWebsite?.urlStatus !== "noURLProvided" ? (
                      <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                        {/* Status Icon */}
                        {supplierWebsite?.urlStatus === "working" ? (
                          <DoneIcon width={20} height={20} />
                        ) : supplierWebsite?.urlStatus === "notWorking" ? (
                          <FailedIcon width={20} height={20} />
                        ) : supplierWebsite?.urlStatus === "notExist" ? (
                          <WarningIcon width={20} height={20} />
                        ) : null}

                        {/* Status text */}
                        <span
                          className={
                            supplierWebsite?.urlStatus === "working"
                              ? "working-website"
                              : supplierWebsite?.urlStatus === "notExist"
                              ? "cannot-reach-website"
                              : "not-working-website"
                          }
                        >
                          {t(supplierWebsite?.urlStatus?.toLowerCase())}
                        </span>
                      </div>
                    ) : null}
                  </section>

                  {/* <SectionDivider title="informationFetched" />
                  <section>
                    <div>
                      <SectionDivider title="email" />
                      <div className="o-row">
                        <p className="u-width--min-width_150">
                          {t("emailFromInvoice")}:{" "}
                        </p>
                        <p>supplier@supplier.com</p>
                      </div>
                      <div className="o-row">
                        <p className="u-width--min-width_150">
                          {t("emailFromWebsite")}:{" "}
                        </p>
                        <p>supplier@supplier.com</p>
                      </div>
                    </div>

                    <div>
                      <SectionDivider title="address" />
                      <div className="o-row">
                        <p className="u-width--min-width_150">
                          {t("addressFromInvoice")}:{" "}
                        </p>
                        <p>SHINJONK BONK MONK</p>
                      </div>
                      <div className="o-row">
                        <p className="u-width--min-width_150">
                          {t("addressFromWebsite")}:{" "}
                        </p>
                        <p>SHINJONK BONK MONK</p>
                      </div>
                    </div>

                    <div>
                      <SectionDivider title="phoneNumber" />
                      <div className="o-row">
                        <p className="u-width--min-width_150">
                          {t("phoneNumberFromInvoice")}:{" "}
                        </p>
                        <p>888-999-667-555</p>
                      </div>
                      <div className="o-row">
                        <p className="u-width--min-width_150">
                          {t("phoneNumberFromWebsite")}:{" "}
                        </p>
                        <p>888-999-667-555</p>
                      </div>
                    </div>
                  </section> */}
                </div>
              </>
            }
          />

          {invoice?.version === 1 ? null : (
            <>
              <SectionDivider title="supplierDetails" />
              <CollapsibleCard
                title={t("supplier")}
                collapsed={false}
                body={
                  <>
                    <div className="o-column o-column--gap-size_300 u-padding--top_300">
                      <section className="o-column o-column--gap-size_300">
                        {/* supplier info from the AI/Invoice */}
                        <SectionDivider title="supplierName" />
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("invoiceSupplierName")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="name"
                              value={companyDetails?.name}
                              onChange={handleCompanyDetailsChange}
                              flex={true}
                            />
                            <input
                              type="radio"
                              name="supplierName"
                              checked={selectedSource === "invoice"}
                              onChange={() => handleRadioChange("invoice")}
                            />
                          </div>
                        </div>

                        {/* supplier info from the database */}
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("databaseSupplierName")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <HotDropdown
                              initOptions={[
                                { id: "All", name: "All" },
                                ...suppliers,
                              ]}
                              selected={supplierDBDetails}
                              setSelected={(supplier) =>
                                setSupplierDBDetails(supplier)
                              }
                              flex
                              getData={getSuppliers}
                              errorText={error}
                              optionsName={"suppliers"}
                            />
                            <input
                              type="radio"
                              name="supplierName"
                              checked={selectedSource === "database"}
                              onChange={() => handleRadioChange("database")}
                            />
                          </div>
                        </div>

                        {/* THE DISPLAY OF STATUS WILL BE ACCORDING TO THE DATA */}
                        <RenderMatchPercentage
                          value1={companyDetails?.name}
                          value2={supplierDBDetails?.name}
                          spanStyle="u-width--min-width_300"
                        />
                      </section>

                      <section className="o-column o-column--gap-size_300">
                        <SectionDivider title="supplierAddress" />
                        <div className={`o-column-row-switcher `}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("invoiceSupplierAddress")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="address"
                              value={companyDetails?.address}
                              onChange={handleCompanyDetailsChange}
                              flex={true}
                            />
                            <input
                              type="radio"
                              name="supplierAddress"
                              checked={selectedSource === "invoice"}
                              onChange={() => handleRadioChange("invoice")}
                            />
                          </div>
                        </div>
                        {/* <SectionDivider title="databaseSupplierAddress" /> */}
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("databaseSupplierAddress")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="address"
                              value={supplierDBDetails?.address}
                              onChange={handleCompanyDBDetailsChange}
                              flex={true}
                            />
                            <input
                              type="radio"
                              name="supplierAddress"
                              checked={selectedSource === "database"}
                              onChange={() => handleRadioChange("database")}
                            />
                          </div>
                        </div>

                        {/* THE DISPLAY OF STATUS WILL BE ACCORDING TO THE DATA */}
                        <RenderMatchPercentage
                          value1={companyDetails?.address}
                          value2={supplierDBDetails?.address}
                          spanStyle="u-width--min-width_300"
                        />
                      </section>

                      <section className="o-column o-column--gap-size_300">
                        <SectionDivider title="supplierEmail" />
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("invoiceSupplierEmail")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="email"
                              value={companyDetails?.email}
                              onChange={handleCompanyDetailsChange}
                              flex={true}
                            />
                            <input
                              type="radio"
                              name="supplierEmail"
                              checked={selectedSource === "invoice"}
                              onChange={() => handleRadioChange("invoice")}
                            />
                          </div>
                        </div>
                        {/* <SectionDivider title="databaseSupplierEmail" /> */}
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("databaseSupplierEmail")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="email"
                              value={supplierDBDetails?.email}
                              onChange={handleCompanyDBDetailsChange}
                              flex={true}
                              // readonly={true}
                            />
                            <input
                              type="radio"
                              name="supplierEmail"
                              checked={selectedSource === "database"}
                              onChange={() => handleRadioChange("invoice")}
                            />
                          </div>
                        </div>

                        {/* DATA STATUS */}
                        <RenderMatchPercentage
                          value1={companyDetails?.email}
                          value2={supplierDBDetails?.email}
                          spanStyle="u-width--min-width_300"
                        />
                      </section>

                      <section className="o-column o-column--gap-size_300">
                        <SectionDivider title="supplierPhone" />
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("invoiceSupplierPhone")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="phone"
                              value={companyDetails?.phone}
                              onChange={handleCompanyDetailsChange}
                              flex={true}
                            />
                            <input
                              type="radio"
                              name="supplierPhone"
                              checked={selectedSource === "invoice"}
                              onChange={() => handleRadioChange("invoice")}
                            />
                          </div>
                        </div>
                        {/* <SectionDivider title="databaseSupplierPhone" /> */}
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("databaseSupplierPhone")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="phone"
                              value={supplierDBDetails?.phone}
                              onChange={handleCompanyDBDetailsChange}
                              flex={true}
                            />
                            <input
                              type="radio"
                              name="supplierPhone"
                              checked={selectedSource === "database"}
                              onChange={() => handleRadioChange("invoice")}
                            />
                          </div>
                        </div>

                        {/* THE DISPLAY OF STATUS WILL BE ACCORDING TO THE DATA */}
                        <RenderMatchPercentage
                          value1={companyDetails?.phone}
                          value2={supplierDBDetails?.phone}
                          spanStyle="u-width--min-width_300"
                        />
                      </section>
                    </div>
                  </>
                }
              />

              {/* BANK SECTION */}
              <SectionDivider title="bankDetails" />
              <CollapsibleCard
                title={t("bank")}
                collapsed={false}
                body={
                  <>
                    {/* NEW: Bank details */}
                    <div className="o-column o-column--gap-size_300 u-flex_flex-1 u-padding--top_300">
                      <section className="o-column o-column--gap-size_300">
                        <SectionDivider title="bankName" />
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("invoiceBankName")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="name"
                              value={bankDetails?.name}
                              onChange={handleBankDetailsChange}
                              flex={true}
                            />
                            <input
                              type="radio"
                              name="bankName"
                              checked={selectedBankSource === "invoice"}
                              onClick={() => handleBankRadioChange("invoice")}
                            />
                          </div>
                        </div>

                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("databaseBankName")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <HotdropdownV2
                              initOptions={banks.map((bank) => ({
                                title: bank.name,
                                value: bank.id,
                              }))}
                              selected={
                                selectedBank || {
                                  title: bankDBDetails.name,
                                  value: bankDBDetails.id,
                                }
                              }
                              setSelected={(bank) => {
                                setSelectedBank(bank);

                                setSelectedBranch(null);
                                handleSelectABranch(bank);
                              }}
                              flex
                              getData={getBanks}
                              optionsName={"bankAccounts"}
                            />

                            <input
                              type="radio"
                              name="bankName"
                              checked={selectedBankSource === "db"}
                              onClick={() => handleBankRadioChange("db")}
                            />
                          </div>
                        </div>

                        {/* Data Matching */}
                        <RenderMatchPercentage
                          value1={bankDetails?.name}
                          value2={bankDBDetails?.name}
                          spanStyle="u-width--min-width_300"
                        />
                      </section>

                      <section className="o-column o-column--gap-size_300">
                        <SectionDivider title="bankBranchAddress" />
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("invoiceBranchAddress")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="address"
                              value={bankDetails?.address || ""}
                              onChange={handleBankDetailsChange}
                              flex={true}
                            />
                            <input
                              type="radio"
                              name="bankAddress"
                              checked={branchAddressSource === "invoice"}
                              onChange={() => setBranchAddressSource("invoice")}
                            />
                          </div>
                        </div>

                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("databaseBranchAddress")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <HotdropdownV2
                              initOptions={selectedBankBranches.map(
                                (branch) => ({
                                  title: branch.name,
                                  value: branch.id,
                                })
                              )}
                              selected={{
                                title: bankDBDetails?.branch?.address,
                                value: bankDBDetails?.branch?.branchId,
                              }}
                              setSelected={(branch) => {
                                handleBranchSelection(branch);
                                handleSelectABranch(branch);
                                setBranchAddressSource("db");
                              }}
                              flex
                              optionsName="branches"
                            />
                            <input
                              type="radio"
                              name="bankAddress"
                              checked={branchAddressSource === "db"}
                              onChange={() => setBranchAddressSource("db")}
                            />
                          </div>
                        </div>

                        <RenderMatchPercentage
                          value1={bankDetails?.address}
                          value2={bankDBDetails?.branch?.address}
                          spanStyle="u-width--min-width_300"
                        />
                      </section>

                      <section className="o-column o-column--gap-size_300">
                        <SectionDivider title="accountNumber" />
                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("invoiceAccountNumber")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <CustomInput
                              name="accountNumber"
                              value={bankDetails?.accountNumber}
                              onChange={handleBankDetailsChange}
                              flex={true}
                            />
                            <input
                              type="radio"
                              name="accountNumber"
                              onClick={() =>
                                setSelectedAccountNumberSource("invoice")
                              }
                            />
                          </div>
                        </div>

                        <div className={`o-column-row-switcher`}>
                          <span className="u-width--min-width_300 o-column o-column--horizontal-align_center">
                            {t("databaseAccountNumber")}:{" "}
                          </span>
                          <div className="u-width_full-width o-row o-row--gap-size_200">
                            <HotdropdownV2
                              key={selectedBranch?.value || "default"}
                              initOptions={getFilteredAccounts().map(
                                (account) => ({
                                  title: account.accountNumber,
                                  value: account.id,
                                  accountNumber: account.accountNumber,
                                })
                              )}
                              selected={selectedBankAccount}
                              setSelected={(account) =>
                                handleBankAccountSelection(account)
                              }
                              flex
                              optionsName="bankAccounts"
                            />
                            <input
                              type="radio"
                              name="accountNumber"
                              onClick={() =>
                                setSelectedAccountNumberSource("db")
                              }
                            />
                          </div>
                        </div>

                        {/* DATA STATUS */}
                        <RenderMatchPercentage
                          value1={bankDetails?.accountNumber}
                          value2={
                            selectedBankAccount?.accountNumber ||
                            bankDBDetails.dbAccountNumber
                          }
                          spanStyle="u-width--min-width_300"
                        />
                      </section>
                    </div>
                  </>
                }
              />
            </>
          )}

          <SectionDivider title="invoiceAndContainersDetails" />
          <CollapsibleCard
            title={t("invoiceAndContainersDetails")}
            collapsed={false}
            body={
              <>
                {/* Invoice details */}
                <div
                  className={`o-column o-column--gap-size_300 u-flex_flex-1 u-padding--top_300 ${
                    analyzeLoading ? "disabled-area" : ""
                  }`}
                >
                  <SectionDivider title="invoiceDetails" />
                  <section>
                    <div className={classes.inputContainer}>
                      <span>{t("subCompany")}: </span>
                      <Dropdown
                        options={subCompanies}
                        selected={selectedSubCompany}
                        setSelected={handleSelectSubCompany}
                        flex={true}
                        getData={getSubCompanies}
                        nameInAr={true}
                      />
                    </div>
                  </section>
                  <section>
                    <div className={classes.inputContainer}>
                      <span>{t("invoiceNumber")}: </span>
                      <div className={classes.invoiceNumberContainer}>
                        <CustomInput
                          name="invoiceNumber"
                          value={invoice?.invoiceNumber}
                          onChange={handleInvoiceInputChange}
                          flex={true}
                        />
                      </div>
                    </div>
                  </section>
                  <section>
                    <div className={classes.inputContainer}>
                      <span>{t("Date")}: </span>
                      <div className={classes.invoiceNumberContainer}>
                        <CustomInput
                          name="date"
                          type="date"
                          value={formatDate(invoice?.date)}
                          onChange={handleInvoiceDateChange}
                          flex={true}
                        />
                      </div>
                    </div>
                  </section>
                  <section>
                    <div className={classes.inputContainer}>
                      <span>{t("amountForSupplier")}: </span>
                      <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                        <CustomInput
                          name="amountForSupplier"
                          value={invoice?.amountForSupplier}
                          onChange={handleAmountChange}
                          flex={true}
                          type="moneyV2"
                        />
                        <div className="t-bold_bold">
                          {selectedSupplier?.currency}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className={classes.section}>
                    <div className={classes.inputContainer}>
                      <span className={classes.notesClass}>{t("notes")}: </span>
                      <div
                        className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
                      >
                        <div className={classes.invoiceNumberContainer}>
                          <CustomInput
                            name="notes"
                            value={invoice?.notes}
                            onChange={handleInvoiceInputChange}
                            type="textarea"
                            flex={true}
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* PRICES COMPARISON */}
                  {/* <section className="o-column o-column--gap-size_300">
                    <SectionDivider title="materialPriceAudit" />
                    <TextDiv
                      title="actualTotalMaterialPrice"
                      value={invoice?.calculatedContainersPrice}
                      divClasses="o-row o-row--vertical-align_center"
                      pClasses="t-text-medium u-width--min-width_250"
                    />
                    <TextDiv
                      title="finalInvoiceWrittenPrice"
                      value={invoice?.invoiceContainersPrice}
                      divClasses="o-row o-row--vertical-align_center"
                      pClasses="t-text-medium u-width--min-width_250"
                    />

                    {RenderMatchStatus(
                      invoice?.calculatedContainersPrice,
                      invoice?.invoiceContainersPrice
                    )}
                  </section> */}
                </div>

                {/* Containers details */}
                <div
                  className={`o-column  u-flex_flex-1 u-padding--top_300 ${
                    analyzeLoading ? "disabled-area" : ""
                  }`}
                >
                  {inputs?.length ? (
                    <>
                      <SectionDivider title="containersDetails" />

                      <div
                        className={`add-shipped-container-container-part ${classes.flexableForm} ${classes.flexableFormWithPadding}`}
                        ref={containerDiv}
                      >
                        {inputs?.map((item, index) => {
                          if (index === page - 1) {
                            return (
                              <>
                                <section
                                  className={`${classes.section} ${classes.invoiceNumber}`}
                                >
                                  <div className={classes.inputContainer}>
                                    <span>{t("containerNumber")}: </span>
                                    <div
                                      className={classes.invoiceNumberContainer}
                                    >
                                      <CustomInput
                                        name="containerNumber"
                                        value={item?.containerNumber || ""}
                                        onChange={(e) =>
                                          handleInputChange(e, page - 1)
                                        }
                                        flex={true}
                                      />
                                    </div>
                                  </div>
                                </section>
                                {/* <section>
                        <div className={classes.inputContainer}>
                          <span>{t("Date")}: </span>
                          <CustomInput
                            name="date"
                            type="date"
                            flex={true}
                            value={formatDate(invoice?.date)}
                            onChange={(e) => handleInvoiceDateChange(e)}
                          />
                        </div>
                      </section> */}

                                <section className={classes.section}>
                                  <div className={classes.inputContainer}>
                                    <span>{t("containerPrice")}: </span>
                                    <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                                      <CustomInputWithUnit
                                        name="amountForSupplier"
                                        value={item?.amountForSupplier}
                                        onChange={(e) =>
                                          handleInputChange(e, page - 1)
                                        }
                                        type="moneyV2"
                                        flex={true}
                                        unit={
                                          item?.unitPriceCurrency === "$"
                                            ? "USD"
                                            : item?.unitPriceCurrency
                                        }
                                      />
                                    </div>
                                  </div>
                                </section>

                                <section className={classes.section}>
                                  <div className={classes.inputContainer}>
                                    <span>{t("expectedArrivalDate")}: </span>
                                    <CustomInput
                                      name="expectedArrivalDate"
                                      type="date"
                                      flex={true}
                                      value={formatDate(
                                        item?.expectedArrivalDate
                                      )}
                                      onChange={handleDateChange}
                                    />
                                  </div>
                                </section>

                                <section
                                  className={`${classes.section} ${classes.invoiceNumber}`}
                                >
                                  <div className={classes.inputContainer}>
                                    <span>{t("finalDestination")}: </span>
                                    <CustomInput
                                      name="finalDestination"
                                      value={item?.finalDestination}
                                      onChange={(e) =>
                                        handleInputChange(e, page - 1)
                                      }
                                      flex={true}
                                    />
                                  </div>
                                </section>

                                <section
                                  className={`${classes.section} ${classes.invoiceNumber}`}
                                >
                                  <div className={classes.inputContainer}>
                                    <span>{t("material")}: </span>
                                    <CustomInput
                                      name="material"
                                      value={item?.material}
                                      onChange={(e) =>
                                        handleInputChange(e, page - 1)
                                      }
                                      flex={true}
                                    />
                                  </div>
                                </section>

                                <section
                                  className={`${classes.section} ${classes.invoiceNumber}`}
                                >
                                  <div className={classes.inputContainer}>
                                    <span>{t("unitPrice")}: </span>
                                    <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                                      <CustomInputWithUnit
                                        name="unitPrice"
                                        value={item?.unitPrice}
                                        onChange={(e) =>
                                          handleInputChange(e, page - 1)
                                        }
                                        flex={true}
                                        type="moneyV2"
                                        unit={
                                          item?.unitPriceCurrency === "$"
                                            ? "USD"
                                            : item?.unitPriceCurrency
                                        }
                                      />
                                    </div>
                                  </div>
                                </section>

                                <section
                                  className={`${classes.section} ${classes.invoiceNumber}`}
                                >
                                  <div className={classes.inputContainer}>
                                    <span>{t("numberOfBoxes")}: </span>
                                    <CustomInput
                                      name="numberOfBoxes"
                                      value={item?.numberOfBoxes}
                                      onChange={(e) =>
                                        handleInputChange(e, page - 1)
                                      }
                                      type="moneyV2"
                                      flex={true}
                                    />
                                  </div>
                                </section>
                                <section
                                  className={`${classes.section} ${classes.invoiceNumber}`}
                                >
                                  <div className={classes.inputContainer}>
                                    <span>{t("measuringUnit")}</span>
                                    <CustomInput
                                      name="measuringUnit"
                                      value={item?.measuringUnit}
                                      onChange={(e) =>
                                        handleInputChange(e, page - 1)
                                      }
                                      flex={true}
                                    />
                                  </div>
                                </section>

                                <section
                                  className={`${classes.section} ${classes.invoiceNumber}`}
                                >
                                  <div className={classes.inputContainer}>
                                    <span>{t("shipper")}: </span>
                                    <CustomInput
                                      name="shipper"
                                      value={item?.shipper}
                                      onChange={(e) =>
                                        handleInputChange(e, page - 1)
                                      }
                                      flex={true}
                                    />
                                  </div>
                                </section>

                                {/* Date of Entry */}
                                <section className={classes.section}>
                                  <div className={classes.inputContainer}>
                                    <span>{t("dateOfEntry")}: </span>
                                    <CustomInput
                                      name="dateOfEntry"
                                      type="date"
                                      flex={true}
                                      value={formatDate(item?.dateOfEntry)}
                                      onChange={handleDateChange}
                                    />
                                  </div>
                                </section>

                                {/* Shipping Date */}
                                <section className={classes.section}>
                                  <div className={classes.inputContainer}>
                                    <span>{t("shippingDate")}: </span>
                                    <CustomInput
                                      name="shippingDate"
                                      type="date"
                                      flex={true}
                                      value={formatDate(item?.shippingDate)}
                                      onChange={handleDateChange}
                                    />
                                  </div>
                                </section>

                                {/* Exit Date */}
                                <section className={classes.section}>
                                  <div className={classes.inputContainer}>
                                    <span>{t("exitDate")}: </span>
                                    <CustomInput
                                      name="exitDate"
                                      type="date"
                                      flex={true}
                                      value={formatDate(item?.exitDate)}
                                      onChange={handleDateChange}
                                    />
                                  </div>
                                </section>

                                {/* Permits Receiving Date */}
                                <section className={classes.section}>
                                  <div className={classes.inputContainer}>
                                    <span>{t("permitsReceivingDate")}: </span>
                                    <CustomInput
                                      name="permitsReceivingDate"
                                      type="date"
                                      flex={true}
                                      value={formatDate(
                                        item?.permitsReceivingDate
                                      )}
                                      onChange={handleDateChange}
                                    />
                                  </div>
                                </section>

                                {/* Notes */}
                                <section className={classes.section}>
                                  <div className={classes.inputContainer}>
                                    <span className={classes.notesClass}>
                                      {t("notes")}:{" "}
                                    </span>
                                    <CustomInput
                                      name="notes"
                                      type="textarea"
                                      value={item?.notes || ""}
                                      onChange={(e) =>
                                        handleInputChange(e, page - 1)
                                      }
                                      flex={true}
                                    />
                                  </div>
                                </section>

                                <PDFUploader
                                  ref={pdfUploaderRef}
                                  invoiceNumber={
                                    shippedContainers?.invoiceNumber
                                  }
                                  invoiceDocs={invoiceDocs}
                                  setInvoiceDocs={setInvoiceDocs}
                                  mode="upload"
                                  documents={shippedContainers?.documents}
                                  tempDocs={invoiceDocs}
                                  setTempDocs={setInvoiceDocs}
                                  invoiceType="multiContainersInvoice"
                                />
                                <div className="o-row o-row--horizontal-align_end u-margin--top_500">
                                  {/* DELETE CONTAINER */}
                                  <button
                                    onClick={handleDeleteContainerClick}
                                    data-container-index={index}
                                    className="temp-delete-button"
                                  >
                                    ❌ {t("delete")}
                                  </button>
                                </div>
                              </>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </>
                  ) : null}
                </div>
                {/* Containers Next and previous buttons */}
                <div>
                  <div className={""}>
                    {inputs?.length ? (
                      <div className={"add-shipped-container-next-previous"}>
                        <CustomButton
                          variant="blank"
                          title={t("previous")}
                          onClick={handlePrevious}
                          disabled={page === 1}
                          icon={
                            currentDirection === "ltr" ? previousIcon : nextIcon
                          }
                          iconSize={15}
                        />
                        <CustomButton
                          title={t("next")}
                          variant="blank"
                          onClick={handleNext}
                          disabled={page === inputs.length}
                          icon={
                            currentDirection === "ltr" ? nextIcon : previousIcon
                          }
                          iconSize={15}
                          reverseIcon
                        />
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={`add-shipped-container-add-another ${
                      analyzeLoading ? "disabled-area" : ""
                    }`}
                  >
                    <AddAnotherButton
                      onClick={handleAddInput}
                      variant="blank"
                      title={t("addContainer")}
                      icon={addIcon}
                      iconSize={16}
                      gender={GENDER.FEMALE}
                      arrayLength={inputs?.length}
                      disabled={loadingModal}
                    />
                  </div>
                </div>
              </>
            }
          />
        </div>

        {/* SAVE AND CANCEL BUTTONS */}
        <div className="u-margin--top_700">
          {loadingModal && <Loader />}
          {error && !loadingModal && (
            <div className="o-row o-row--horizontal-align_end">
              <ErrorMessage>{t(error)}</ErrorMessage>
            </div>
          )}
          <section className={classes.actions}>
            <CustomButton
              onClick={handleAddRow}
              title={t("save")}
              icon={saveIcon}
              iconSize={16}
              disabled={loadingModal}
            />
            <CustomButton
              title={t("cancel")}
              onClick={handleCancel}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
          </section>
        </div>
      </div>

      {showDeleteModal && (
        <AreYouSureModal
          onClose={handleCancelDelete} // Close the modal
          onConfirm={handleConfirmDelete} // Confirm deletion
          customMessage="confirmDeleteContainer"
          closeTitle="cancel"
          confirmTitle="delete"
        />
      )}
    </PageWrapper>
  );
};

export default AnalyzedNoCustomerInvoice;
